import React, { useState, useMemo } from "react";
import DatePicker from "../../../../components/inputs/MomentDatePicker";
import { Table, Button, Tag, Modal, Input, Select, Checkbox, message, Spin } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import { useQueryClient } from "react-query";
import {
  useCreatePermissionRequest,
  useGetAllPermissions,
} from "../../../../services/permissions.service";

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;

const Leaves = () => {
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days"),
    moment(),
  ]);
  const [typeFilter, setTypeFilter] = useState("all");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [formData, setFormData] = useState({
    type: "leave",
    reason: "",
    dateRange: [null, null],
    halfDaySelected: false,
    halfDaySession: "morning",
  });
  const [daysCount, setDaysCount] = useState(1);

  const leaveParams = useMemo(() => {
    return {
      start_date: dateRange[0]?.format("YYYY-MM-DD"),
      end_date: dateRange[1]?.format("YYYY-MM-DD"),
      type: "leave",
    };
  }, [dateRange]);

  const sickParams = useMemo(() => {
    return {
      start_date: dateRange[0]?.format("YYYY-MM-DD"),
      end_date: dateRange[1]?.format("YYYY-MM-DD"),
      type: "sick",
    };
  }, [dateRange]);

  // Fetch leave requests
  const {
    data: leaveData,
    isLoading: leaveLoading,
    refetch: refetchLeaves,
  } = useGetAllPermissions({}, leaveParams);

  // Fetch sick leave requests
  const {
    data: sickData,
    isLoading: sickLoading,
    refetch: refetchSick,
  } = useGetAllPermissions({}, sickParams);

  const data = useMemo(() => {
    let allData = [...(leaveData || []), ...(sickData || [])];

    allData.sort((a, b) => {
      const dateA = moment(a.from_date || a.from_time || a.created_at);
      const dateB = moment(b.from_date || b.from_time || b.created_at);
      return dateB.valueOf() - dateA.valueOf();
    });

    if (typeFilter && typeFilter !== "all") {
      return allData.filter((item) => item.type === typeFilter);
    }
    return allData;
  }, [leaveData, sickData, typeFilter]);

  const isLoading = leaveLoading || sickLoading;

  const createLeave = useCreatePermissionRequest({
    onSuccess: (response) => {
      message.success("Leave request submitted successfully");
      queryClient.invalidateQueries("allPermissions");
      refetchLeaves();
      refetchSick();
      handleCancel();
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.detail || "Failed to submit request",
      );
    },
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "green";
      case "pending":
        return "orange";
      case "rejected":
        return "red";
      default:
        return "default";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "sick":
        return "red";
      case "leave":
        return "orange";
      default:
        return "default";
    }
  };

  const getTypeDisplayName = (type) => {
    switch (type) {
      case "sick":
        return "Sick Leave";
      case "leave":
        return "Casual Leave";
      default:
        return type;
    }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleTypeFilterChange = (value) => {
    setTypeFilter(value);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setFormData({
      type: "leave",
      reason: "",
      dateRange: [null, null],
      halfDaySelected: false,
      halfDaySession: "morning",
    });
    setDaysCount(1);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "dateRange" || field === "halfDaySelected") {
        const [start, end] = field === "dateRange" ? value : updated.dateRange;
        if (start && end) {
          const days = end.diff(start, "days") + 1;
          setDaysCount(updated.halfDaySelected ? 0.5 : days);
        } else {
          setDaysCount(1);
        }
      }
      return updated;
    });
  };

  const handleSave = () => {
    const {
      type,
      reason,
      dateRange: selectedRange,
      halfDaySelected,
      halfDaySession,
    } = formData;
    if (!type || !reason || !selectedRange[0] || !selectedRange[1]) {
      message.error("Please fill all required fields");
      return;
    }

    const fromStr = selectedRange[0].format("YYYY-MM-DD");
    const toStr = selectedRange[1].format("YYYY-MM-DD");
    const allLeaves = [...(leaveData || []), ...(sickData || [])];

    const dateConflict = allLeaves.find(
      (r) =>
        r.status !== "rejected" &&
        r.from_date &&
        r.to_date &&
        r.from_date <= toStr &&
        r.to_date >= fromStr,
    );
    if (dateConflict) {
      message.warning(
        `A ${dateConflict.type} request already exists for the selected dates and is ${dateConflict.status}.`,
      );
      return;
    }
    const pendingConflict = allLeaves.find(
      (r) => r.type === type && r.status === "pending",
    );
    if (pendingConflict) {
      message.warning(
        `You already have a pending ${type} request. Please wait for it to be reviewed.`,
      );
      return;
    }

    const requestData = {
      type: type,
      reason: reason,
      leave_duration: halfDaySelected
        ? halfDaySession === "morning"
          ? "half_day_morning"
          : "half_day_afternoon"
        : "full_day",
      from_date: selectedRange[0].format("YYYY-MM-DD"),
      to_date: selectedRange[1].format("YYYY-MM-DD"),
      num_days: halfDaySelected
        ? 0.5
        : selectedRange[1].diff(selectedRange[0], "days") + 1,
    };

    createLeave.mutate(requestData);
  };

  const columns = [
    {
      title: "Date Range",
      key: "date_range",
      align: "center",
      render: (_, record) => {
        if (record.from_date && record.to_date) {
          const startDate = moment(record.from_date).format("DD/MM/YYYY");
          const endDate = moment(record.to_date).format("DD/MM/YYYY");
          return (
            <div className="font-semibold">
              {startDate === endDate ? startDate : `${startDate} to ${endDate}`}
            </div>
          );
        }
        return (
          <div className="font-semibold">
            {record.from_time
              ? moment(record.from_time).format("DD/MM/YYYY")
              : moment(record.created_at).format("DD/MM/YYYY")}
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      align: "center",
      render: (text) => (
        <Tag color={getTypeColor(text)} className="capitalize">
          {getTypeDisplayName(text)}
        </Tag>
      ),
    },
    {
      title: "Duration",
      dataIndex: "num_days",
      key: "leave_duration",
      align: "center",
      render: (num_days, record) => {
        if (record.leave_duration === "half_day_morning") {
          return (
            <span className="text-gray-700 font-semibold">
              Half Day (Morning)
            </span>
          );
        }
        if (record.leave_duration === "half_day_afternoon") {
          return (
            <span className="text-gray-700 font-semibold">
              Half Day (Evening)
            </span>
          );
        }
        // Fallback: compute from dates if num_days is missing (old records / mobile)
        const resolvedDays =
          num_days != null
            ? num_days
            : record.from_date && record.to_date
              ? moment(record.to_date).diff(moment(record.from_date), "days") +
                1
              : null;
        return (
          <span className="text-gray-700 font-semibold">
            {record.leave_duration === "half_day" || resolvedDays === 0.5
              ? record.hours || "Half Day"
              : resolvedDays != null
                ? `${resolvedDays} day${resolvedDays !== 1 ? "s" : ""}`
                : "—"}
          </span>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      align: "center",
      render: (text) => <span className="text-gray-600">{text || "-"}</span>,
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      render: (_, record) => (
        <Tag
          color={getStatusColor(record.status)}
          className="capitalize px-3 py-1 font-bold"
        >
          {record.status === "approved"
            ? "Approved"
            : record.status === "rejected"
              ? "Rejected"
              : "Pending"}
        </Tag>
      ),
    },
  ];

  return (
    <div className="mt-8 min-h-screen">
      <div className="bg-white drop-shadow-md p-6 rounded-lg">
        <div className="text-xl text-orange-500 font-semibold py-4">
          Staff Attendance - Leaves & Time Off
        </div>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-base font-bold text-gray-700">
              Select Date Range:
            </span>
            <RangePicker
              format="DD/MM/YYYY"
              value={dateRange}
              onChange={handleDateRangeChange}
              style={{ width: 300 }}
            />
            <span className="text-base font-bold text-gray-700 ml-4">
              Filter by Type:
            </span>
            <Select
              value={typeFilter}
              onChange={handleTypeFilterChange}
              style={{ width: 150 }}
            >
              <Option value="all">All Types</Option>
              <Option value="leave">Casual Leave</Option>
              <Option value="sick">Sick Leave</Option>
            </Select>
          </div>
          <Button
            type="primary"
            onClick={showModal}
            className="bg-blue-600 hover:bg-blue-700 font-semibold px-8 py-2 h-10 rounded-xl shadow-lg mb-4"
          >
            Request Leave
          </Button>
        </div>

        <div className="rounded-xl overflow-hidden border-2 border-gray-200 shadow-md">
          <Table
            columns={columns}
            dataSource={data?.length > 0 ? data : []}
            loading={isLoading}
            rowKey="_id"
            rowClassName="hover:bg-blue-50 transition-colors duration-200"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </div>
      </div>

      <Modal
        title={
          <div className="text-xl font-bold text-gray-800">
            Request Leave / Time Off
          </div>
        }
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSave}
            loading={createLeave.isLoading}
            className="bg-blue-600"
          >
            Submit Request
          </Button>,
        ]}
        width={600}
        closeIcon={<CloseOutlined />}
        className="rounded-2xl"
      >
        <div className="flex flex-col gap-4 mt-4">
          <div>
            <label className="block mb-2 font-semibold text-gray-700">
              Type
            </label>
            <Select
              value={formData.type}
              onChange={(value) => handleFormChange("type", value)}
              style={{ width: "100%" }}
            >
              <Option value="leave">Casual Leave</Option>
              <Option value="sick">Sick Leave</Option>
            </Select>
          </div>

          <div>
            <label className="block mb-2 font-semibold text-gray-700">
              Date Range
            </label>
            <RangePicker
              value={formData.dateRange}
              onChange={(dates) => handleFormChange("dateRange", dates)}
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              disabledDate={(current) => current && current < moment().startOf("day")}
            />
          </div>

          {formData.dateRange[0] && formData.dateRange[1] && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-800 font-medium">
                {formData.dateRange[0].isSame(formData.dateRange[1], "day")
                  ? `Selected Date: ${formData.dateRange[0].format(
                      "DD/MM/YYYY",
                    )}`
                  : `Selected Date Range: ${formData.dateRange[0].format(
                      "DD/MM/YYYY",
                    )} to ${formData.dateRange[1].format("DD/MM/YYYY")}`}
              </div>
              <div className="text-base text-blue-600 font-bold mt-1">
                Total Days: {daysCount} day{daysCount !== 1 ? "s" : ""}
              </div>
            </div>
          )}

          <div>
            <Checkbox
              checked={formData.halfDaySelected}
              onChange={(e) =>
                handleFormChange("halfDaySelected", e.target.checked)
              }
            >
              Half Day Leave
            </Checkbox>
          </div>

          {formData.halfDaySelected && (
            <div className="flex gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <Checkbox
                checked={formData.halfDaySession === "morning"}
                onChange={(e) => {
                  if (e.target.checked)
                    handleFormChange("halfDaySession", "morning");
                }}
              >
                Morning
              </Checkbox>
              <Checkbox
                checked={formData.halfDaySession === "afternoon"}
                onChange={(e) => {
                  if (e.target.checked)
                    handleFormChange("halfDaySession", "afternoon");
                }}
              >
                Evening
              </Checkbox>
            </div>
          )}

          <div>
            <label className="block mb-2 font-semibold text-gray-700">
              Reason
            </label>
            <TextArea
              rows={4}
              value={formData.reason}
              onChange={(e) => handleFormChange("reason", e.target.value)}
              placeholder="Enter reason for leave"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Leaves;
